import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { readCookie } from '../core/cookies';
import { RegistrationFormsService } from './forms/registration-forms.service';

const MEWS_NAME = 'extra_data[integration][name]';
const MEWS_TOKEN = 'extra_data[integration][configuration][accessToken]';

export function queryParams(search: string): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(search));
}

@Injectable({ providedIn: 'root' })
export class RegistrationService {
  private readonly http = inject(HttpClient);
  private readonly forms = inject(RegistrationFormsService);
  private extraData: Record<string, unknown> = {};

  lang = '';
  resellerHash?: string;
  turnstileToken: string | null = null;

  appendExtraData(data: Record<string, unknown>) {
    this.extraData = { ...this.extraData, ...data };
  }

  /**
   * Reads the partner data from the start-up query params (and cookies). Returns the param
   * names that carry tokens, so the caller can take them out of the address bar.
   */
  readParams(params: Record<string, string>): string[] {
    this.resellerHash = readCookie('campaign_broker') || params['resellerHash'] || undefined;

    const irpid = params['irpid'] || readCookie('irpid');
    const irclickid = params['irclickid'] || readCookie('irclickid');
    this.appendExtraData({ irpid, irclickid });

    if (params[MEWS_NAME] && params[MEWS_TOKEN]) {
      this.appendExtraData({
        integration: { name: params[MEWS_NAME], configuration: { accessToken: params[MEWS_TOKEN] } },
      });
    }
    return ['irpid', 'irclickid', MEWS_NAME, MEWS_TOKEN].filter((key) => key in params);
  }

  payload() {
    const extra_data = Object.fromEntries(Object.entries(this.extraData).filter(([, value]) => value !== undefined));
    return {
      ...this.forms.step1.getRawValue(),
      ...this.forms.step2.getRawValue(),
      lang: this.lang,
      ...(this.resellerHash ? { resellerHash: this.resellerHash } : {}),
      extra_data,
      ...(this.turnstileToken ? { turnstileToken: this.turnstileToken } : {}),
    };
  }

  /** Resolves with where to send the browser: the API's `redirect`, or the new store's sign-in when it gives none. */
  async submit(): Promise<string> {
    const response = await firstValueFrom(
      this.http.post<{ redirect?: string }>(`${environment.API_ENDPOINT}/register`, this.payload()),
    );
    return response.redirect || this.storeSignInUrl(this.forms.step2.controls.domain.value);
  }

  /** Where the new store's staff sign-in lives; the fallback when the API answer cannot be read. */
  storeSignInUrl(domain: string, envName = environment.ENV_NAME): string {
    const host = { staging: 'https://stagingvouchercart.com', 'local-testing': 'http://vouchercart.localhost' }[envName];
    const [scheme, base] = (host ?? 'https://vouchercart.com').split('://');
    return `${scheme}://${domain}.${base}/staff/sign-in`;
  }
}
